/*******************************************************
 * Copyright (C) BoostCommerce.net
 *
 * This file is part of commerical BoostCommerce.net projects.
 *
 * This file can not be copied and/or distributed without the express
 * permission of BoostCommerce.net
 *
 * @Date:   2020-01-20 09:04:57
 *
 *******************************************************/
// @flow

import _ from 'lodash';

import type { MetafieldState, MetafieldAction } from 'stores/flow-type/metafield.type';

import {
  GET_METAFIELDS_REQUEST,
  GET_METAFIELDS_SUCCESS,
  GET_METAFIELDS_FAILURE,
  UPDATE_METAFIELD_REQUEST,
  UPDATE_METAFIELD_SUCCESS,
  UPDATE_METAFIELD_FAILURE,
  DELETE_METAFIELD_REQUEST,
  DELETE_METAFIELD_SUCCESS,
  DELETE_METAFIELD_FAILURE,
  CREATE_METAFIELD_REQUEST,
  CREATE_METAFIELD_SUCCESS,
  CREATE_METAFIELD_FAILURE
} from 'stores/flow-type/metafield.type';

const initialState: MetafieldState = {
  loading: false,
  saving: false,
  deleting: false,
  reordering: false,
  scheduleSyncIn: 0,
  scheduleSyncAfter: 0,
  waitSyncAt: 0,

  success: false,
  error: null,
  metafields: [],
  total: 0
};

const metafieldReducers = (state: FilterDataState = initialState, action: MetafieldAction) => {
  switch (action.type) {
    // Get metafields
    case GET_METAFIELDS_REQUEST:
      return {
        ...state,
        success: false,
        loading: true,
        waitSyncAt: 0,
        scheduleSyncIn: 0,
        scheduleSyncAfter: 0
      };
    case GET_METAFIELDS_SUCCESS: {
      const { data } = action.payload;
      let {
        data: metafields,
        total,
        waitSyncAt,
        scheduleSyncIn,
        scheduleSyncAfter,
        invalid: invalidMetafields = [],
        empty: emptyMetafields = []
      } = data;

      // Remove invalidMetafields if existing in emptyMetafields
      _.remove(invalidMetafields, metafield => {
        const { namespace, key } = metafield;
        return !_.includes(emptyMetafields, { namespace, key });
      });

      return {
        ...state,
        loading: false,
        success: true,
        error: undefined,
        metafields,
        total,
        invalidMetafields,
        emptyMetafields,
        waitSyncAt,
        scheduleSyncIn,
        scheduleSyncAfter
      };
    }
    case GET_METAFIELDS_FAILURE:
      return {
        ...state,
        loading: false,
        success: false,
        error: action.payload
      };

    // Update merchandising rule
    case UPDATE_METAFIELD_REQUEST:
      return {
        ...state,
        success: false,
        saving: true,
        waitSyncAt: 0,
        scheduleSyncIn: 0,
        scheduleSyncAfter: 0
      };
    case UPDATE_METAFIELD_SUCCESS: {
      const { data, waitSyncAt, scheduleSyncIn, scheduleSyncAfter } = action.payload;
      const { data: metafields, total } = _.pick(data, ['data', 'total']);

      // Save updated merchandising to store
      return {
        ...state,
        saving: false,
        success: true,
        metafields,
        total,
        scheduleSyncIn,
        scheduleSyncAfter,
        waitSyncAt,
        error: undefined
      };
    }
    case UPDATE_METAFIELD_FAILURE:
      return {
        ...state,
        saving: false,
        success: false,
        error: action.payload
      };

    // Delete merchandising rule
    case DELETE_METAFIELD_REQUEST:
      return {
        ...state,
        success: false,
        deleting: true,
        waitSyncAt: 0,
        scheduleSyncIn: 0,
        scheduleSyncAfter: 0
      };
    case DELETE_METAFIELD_SUCCESS: {
      const { data, scheduleSyncIn, scheduleSyncAfter, waitSyncAt } = action.payload;

      const { data: allMetafields } = _.pick(data, ['data', 'total']);

      // Filter single metafields by type
      const metafields = _.filter(
        allMetafields,
        metafield =>
          metafield.type === 'metafield_custom_single_metafield' && _.has(metafield, 'appliesTo')
      );

      return {
        ...state,
        deleting: false,
        success: true,
        metafields,
        total: metafields.length,
        scheduleSyncIn,
        scheduleSyncAfter,
        waitSyncAt,
        error: undefined
      };
    }
    case DELETE_METAFIELD_FAILURE:
      return {
        ...state,
        deleting: false,
        success: false,
        error: action.payload
      };

    // Create metafield
    case CREATE_METAFIELD_REQUEST:
      return {
        ...state,
        success: false,
        saving: true,
        waitSyncAt: 0,
        scheduleSyncIn: 0,
        scheduleSyncAfter: 0
      };
    case CREATE_METAFIELD_SUCCESS: {
      const { data, scheduleSyncIn, scheduleSyncAfter, waitSyncAt } = action.payload;

      const { data: metafields, total } = _.pick(data, ['data', 'total']);

      return {
        ...state,
        saving: false,
        metafields,
        total,
        success: true,
        scheduleSyncIn,
        scheduleSyncAfter,
        waitSyncAt,
        error: undefined
      };
    }
    case CREATE_METAFIELD_FAILURE:
      return {
        ...state,
        saving: false,
        success: false,
        error: action.payload
      };

    default:
      return state;
  }
};
export default metafieldReducers;
